import type { JSX, ReactNode } from 'react';
import React, {
  cloneElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { PopupActions } from 'reactjs-popup/dist/types';

import type { ButtonIcon, ButtonVariant } from 'components/atoms/Button';
import Button from 'components/atoms/Button';
import type { CalloutProps } from 'components/atoms/Callout';
import Callout from 'components/atoms/Callout';
import { IconButton } from 'components/atoms/IconButton';
import { CloseIcon } from 'components/atoms/Icons';
import { Stack } from 'components/atoms/Stack';
import type { ModalProps } from 'components/molecules/LegacyModal';
import LegacyModal from 'components/molecules/LegacyModal';
import { useBreakpoint } from './../../../hooks/useBreakpoint';
import * as Styled from './ConfirmationModal.styled';

type ButtonVariants = keyof typeof ButtonVariant;

const getSubtitle = (subtitle?: ReactNode) => {
  if (!subtitle) {
    return null;
  }
  if (typeof subtitle === 'string') {
    return <Styled.Subtitle>{subtitle}</Styled.Subtitle>;
  }

  return subtitle;
};

/**
 * @deprecated This component is deprecated in favor of the molecules/Modal
 */
export interface ConfirmationModalProps extends Omit<ModalProps, 'children'> {
  title?: string | JSX.Element;
  subtitle?: ReactNode;
  content?: string | JSX.Element;
  // biome-ignore lint/suspicious/noConfusingVoidType: void is needed here so callers typed as () => void are assignable
  onConfirm?: () => boolean | void | Promise<boolean | void>;
  buttonText?: string;
  secondaryButtonText?: string;
  cancelText?: string;
  leftButtonText?: string;
  confirmButtonVariant?: ButtonVariants;
  secondaryButtonVariant?: ButtonVariants;
  cancelButtonVariant?: ButtonVariants;
  onClose?: () => void;
  hasCloseIcon?: boolean;
  confirmDisabled?: boolean;
  enableLoading?: boolean;
  confirmRequesting?: boolean;
  className?: string;
  contentStyle?: any;
  disableConfirmOnEnter?: boolean;
  hasCancelButton?: boolean;
  closeOnEscape?: boolean;
  onConfirmDDActionName?: string;
  onCancelDDActionName?: string;
  when?: boolean;
  skipCloseAndConfirm?: boolean;
  skipCloseModal?: boolean;
  onCancelButtonClick?: () => void;
  onConfirmButtonClick?: (e?: any) => void;
  onSecondaryButtonClick?: () => void;
  confirmButtonStartAdornment?: ButtonIcon;
  confirmButtonEndAdornment?: ButtonIcon;
  calloutProps?: CalloutProps;
  lazyContent?: boolean;
}

/**
 * @deprecated This component is deprecated in favor of the molecules/Modal
 * @example <Modal.Root>
 *   <Modal.Trigger>Open Modal</Modal.Trigger>
 *   <Modal.Content>
 *     <Modal.Title>Title</Modal.Title>
 *     Body
 *     <Modal.Footer
 *       dismiss={{ onClick: () => {} }}
 *       primary={{ onClick: () => {} }}
 *       secondary={{ onClick: () => {} }}
 *     />
 *   </Modal.Content>
 * </Modal.Root>
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = React.memo(
  ({
    trigger,
    title,
    subtitle,
    content,
    onConfirm,
    buttonText = 'Confirm',
    secondaryButtonText,
    cancelText = 'Cancel',
    confirmButtonVariant = 'PRIMARY',
    confirmButtonStartAdornment,
    confirmButtonEndAdornment,
    secondaryButtonVariant = 'SECONDARY',
    cancelButtonVariant = 'SECONDARY',
    onClose,
    onOpen,
    className,
    onConfirmDDActionName,
    onCancelDDActionName,
    lazyContent = false,
    hasCloseIcon = false,
    hasCancelButton = true,
    confirmDisabled = false,
    enableLoading = false,
    confirmRequesting = false,
    disableConfirmOnEnter = false,
    closeOnEscape = true,
    size = 'sm',
    when = true,
    skipCloseAndConfirm = false,
    skipCloseModal = false,
    onCancelButtonClick,
    onConfirmButtonClick,
    onSecondaryButtonClick,
    calloutProps,
    ...props
  }: ConfirmationModalProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const popupRef = useRef<PopupActions>(null);
    const close = popupRef.current?.close;
    const isDesktop = useBreakpoint('sm');
    const confirmAndClose = useCallback(
      (
        event: KeyboardEvent | React.MouseEvent<HTMLButtonElement, MouseEvent>,
      ) => {
        event.preventDefault();
        event.stopPropagation();
        if (disableConfirmOnEnter && (event as KeyboardEvent).key === 'Enter') {
          return;
        }
        const confirmed = onConfirm?.();
        if (confirmed === false) {
          return;
        }
        setConfirmed(true);
        if (!enableLoading) {
          close?.();
        }
      },
      [disableConfirmOnEnter, onConfirm, enableLoading, close],
    );

    const isInteractableElement = (el: Element | null) => {
      if (!el) {
        return false;
      }
      const tag = el.tagName.toLowerCase();
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        tag === 'body'
      ) {
        return true;
      }
      if ((el as HTMLElement).isContentEditable) {
        return true;
      }
      return false;
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    const onKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (
          event.key === 'Enter' &&
          isOpen &&
          !isInteractableElement(document.activeElement)
        ) {
          confirmAndClose(event);
        }
      },
      [isOpen, confirmAndClose],
    );

    useEffect(() => {
      if (confirmed && !confirmRequesting) {
        close?.();
        setConfirmed(false); // reset state
      }
    }, [close, confirmRequesting, confirmed]);

    useEffect(() => {
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [onKeyDown]);

    const onOpenHandler = useCallback(() => {
      setIsOpen(true);
      onOpen?.();
    }, [onOpen]);

    const onCloseHandler = useCallback(() => {
      setIsOpen(false);
      onClose?.();
    }, [onClose]);

    if (!when && trigger && React.isValidElement(trigger)) {
      return cloneElement(trigger, { onClick: onConfirm } as any);
    }

    const hasSecondaryButton = !!secondaryButtonText;

    const subtitleToRender = getSubtitle(subtitle);

    return (
      <LegacyModal
        ref={popupRef}
        className={className}
        closeOnEscape={closeOnEscape}
        size={size}
        trigger={trigger}
        onClose={onCloseHandler}
        onOpen={onOpenHandler}
        {...props}
      >
        <Styled.ModalWrapper data-test-id="confirmation-modal">
          <Stack direction="column" spacing="sm1">
            <Styled.HeaderWrapper>
              {title ? (
                <Styled.ModalTitle as="h2">{title}</Styled.ModalTitle>
              ) : null}
              {hasCloseIcon && (
                <IconButton onClick={close}>
                  <CloseIcon color="iconSecondary" />
                </IconButton>
              )}
            </Styled.HeaderWrapper>
            {subtitleToRender}
          </Stack>
          {calloutProps && <Callout {...calloutProps} />}
          {Boolean(content) && (
            <Styled.ModalContent
              data-test-id="input-field"
              direction="column"
              spacing="sm3"
            >
              {isOpen || !lazyContent ? content : null}
            </Styled.ModalContent>
          )}

          <Styled.Actions
            direction={isDesktop ? 'row' : 'column-reverse'}
            hasLeftButton={hasSecondaryButton}
            spacing={isDesktop ? 'none' : 'xs3'}
          >
            {hasCancelButton && hasSecondaryButton && (
              <Button
                data-dd-action={onCancelDDActionName}
                disabled={confirmRequesting}
                label={cancelText}
                variant={cancelButtonVariant}
                onClick={() => {
                  onCancelButtonClick?.();
                  !skipCloseModal && close?.();
                }}
              />
            )}
            <Stack
              direction={isDesktop ? 'row' : 'column-reverse'}
              spacing="xs3"
            >
              {hasCancelButton && !hasSecondaryButton && (
                <Button
                  disabled={confirmRequesting}
                  label={cancelText}
                  variant={cancelButtonVariant}
                  onClick={() => {
                    onCancelButtonClick?.();
                    !skipCloseModal && close?.();
                  }}
                />
              )}

              {hasSecondaryButton && (
                <Button
                  label={secondaryButtonText}
                  variant={secondaryButtonVariant}
                  onClick={() => {
                    onSecondaryButtonClick?.();
                  }}
                />
              )}

              <Button
                data-dd-action={onConfirmDDActionName}
                data-test-id={`popup-button-${buttonText}`}
                disabled={confirmDisabled}
                endAdornment={confirmButtonEndAdornment}
                label={buttonText}
                loading={confirmRequesting}
                startAdornment={confirmButtonStartAdornment}
                variant={confirmButtonVariant}
                onClick={e => {
                  onConfirmButtonClick?.(e);
                  if (!skipCloseAndConfirm) {
                    confirmAndClose?.(e);
                  }
                }}
              />
            </Stack>
          </Styled.Actions>
        </Styled.ModalWrapper>
      </LegacyModal>
    );
  },
);
